import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { appleLogin, linkAppleToBackend } from '../services/authService';
import '../styles/Auth.css';

type AppleSetupMode = 'setup' | 'link';
type LinkStep = 'confirm' | 'password' | 'code';

interface AppleSetupState {
  mode?: AppleSetupMode;
  appleResponse?: any;
  identityToken?: string;
  appleUserId?: string;
  userInfo?: {
    email?: string;
    name?: string;
    picture?: string;
  };
  message?: string;
  linkingProcessId?: string;
  requiresPassword?: boolean;
  requiresLinkApproval?: boolean;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseDisplayName(name: string) {
  const trimmed = (name || '').trim();
  if (!trimmed) return { firstName: '', lastName: '' };

  const cleaned = trimmed.replace(/\s+Apple$/i, '').trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
}

function validatePassword(password: string) {
  if (password.length < 8) return 'Password must be at least 8 characters long';
  if (!/(?=.*[a-z])/.test(password)) return 'Password must contain at least one lowercase letter';
  if (!/(?=.*[A-Z])/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/(?=.*\d)/.test(password)) return 'Password must contain at least one number';
  return null;
}

const AppleSetup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshAuthState } = useAuth();

  const state = (location.state || {}) as AppleSetupState;
  const appleResponse = state.appleResponse || {};

  const identityToken =
    state.identityToken ||
    appleResponse.authorization?.id_token ||
    appleResponse.identityToken ||
    appleResponse.id_token ||
    '';

  const appleUserId =
    state.appleUserId ||
    (typeof appleResponse.user === 'string' ? appleResponse.user : '') ||
    appleResponse.userId ||
    '';

  const userInfo = state.userInfo || {};

  const initialDisplayName =
    userInfo.name ||
    appleResponse.name ||
    [appleResponse.firstName, appleResponse.lastName].filter(Boolean).join(' ') ||
    '';

  const parsedName = useMemo(() => parseDisplayName(initialDisplayName), [initialDisplayName]);

  const [activeMode, setActiveMode] = useState<AppleSetupMode>(state.mode || (state.requiresLinkApproval ? 'link' : 'setup'));
  const [linkStep, setLinkStep] = useState<LinkStep>('confirm');
  const [email, setEmail] = useState(userInfo.email || appleResponse.email || '');
  const [firstName, setFirstName] = useState(parsedName.firstName);
  const [lastName, setLastName] = useState(parsedName.lastName);
  const [phone, setPhone] = useState(appleResponse.phone || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [linkingProcessId, setLinkingProcessId] = useState(state.linkingProcessId || '');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(state.message || '');

  const avatarSrc = useMemo(() => {
    if (userInfo.picture && typeof userInfo.picture === 'string' && userInfo.picture.trim()) {
      return userInfo.picture;
    }
    if (appleResponse.picture && typeof appleResponse.picture === 'string' && appleResponse.picture.trim()) {
      return appleResponse.picture;
    }
    return '/assets/logo2.webp';
  }, [appleResponse.picture, userInfo.picture]);

  useEffect(() => {
    if (!email && userInfo.email) {
      setEmail(userInfo.email);
    }
    if (!firstName && parsedName.firstName) {
      setFirstName(parsedName.firstName);
    }
    if (!lastName && parsedName.lastName) {
      setLastName(parsedName.lastName);
    }
  }, [email, firstName, lastName, parsedName.firstName, parsedName.lastName, userInfo.email]);

  const finishAuth = async () => {
    refreshAuthState();
    navigate('/', { replace: true });
  };

  const handleSetupSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!identityToken) {
      setError('Apple setup state is missing. Please sign in again.');
      return;
    }

    const trimmedEmail = email.trim();
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedPhone = phone.trim();

    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await appleLogin(
        {
          ...appleResponse,
          identityToken,
          authorization: appleResponse.authorization || { id_token: identityToken, code: appleResponse.authorizationCode },
          id_token: appleResponse.id_token || identityToken,
          user: appleResponse.user ?? appleUserId,
          email: trimmedEmail,
          firstName: trimmedFirstName,
          lastName: trimmedLastName,
          phone: trimmedPhone,
        },
        password,
      );

      if (result?.accessToken || result?.token) {
        await finishAuth();
        return;
      }

      if (result?.requiresLinkApproval) {
        setActiveMode('link');
        setLinkStep('confirm');
        setError(result.message || 'An account already exists with this email. Please confirm linking.');
        return;
      }

      if (result?.requiresPassword) {
        setError(result.message || 'Apple sign-in needs additional setup.');
        return;
      }

      setError('Apple sign-in failed. Please try again.');
    } catch (err: any) {
      setError(err.message || 'Apple sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartLink = async () => {
    if (!identityToken || !appleUserId) {
      setError('Apple linking state is missing. Please sign in again.');
      return;
    }

    const trimmedEmail = email.trim();
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setError('Please enter the email address tied to the existing account.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await linkAppleToBackend(trimmedEmail, appleUserId, undefined, identityToken);

      if (result?.requiresPassword) {
        setLinkStep('password');
        return;
      }

      if (result?.requiresVerificationCode) {
        setLinkingProcessId(result.linkingProcessId || '');
        setLinkStep('code');
        return;
      }

      if (result?.accessToken || result?.token) {
        await finishAuth();
        return;
      }

      setError(result.message || 'Unable to start account linking.');
    } catch (err: any) {
      setError(err.message || 'Unable to start account linking.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const trimmedEmail = email.trim();
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await linkAppleToBackend(trimmedEmail, appleUserId, password, identityToken);

      if (result?.requiresVerificationCode) {
        setLinkingProcessId(result.linkingProcessId || '');
        setLinkStep('code');
        return;
      }

      if (result?.accessToken || result?.token) {
        await finishAuth();
        return;
      }

      setError(result.message || 'Unable to verify your password.');
    } catch (err: any) {
      setError(err.message || 'Unable to verify your password.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (verificationCode.trim().length !== 6) {
      setError('Please enter the 6-digit verification code.');
      return;
    }

    if (!linkingProcessId) {
      setError('Missing linking request. Please start the link step again.');
      return;
    }

    const trimmedEmail = email.trim();
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await linkAppleToBackend(
        trimmedEmail,
        appleUserId,
        password || undefined,
        identityToken,
        verificationCode.trim(),
        linkingProcessId,
      );

      if (result?.accessToken || result?.token) {
        await finishAuth();
        return;
      }

      setError(result.message || 'Unable to verify the confirmation code.');
    } catch (err: any) {
      setError(err.message || 'Unable to verify the confirmation code.');
    } finally {
      setLoading(false);
    }
  };

  const isSetupMode = activeMode === 'setup';
  const isLinkConfirm = activeMode === 'link' && linkStep === 'confirm';
  const isLinkPassword = activeMode === 'link' && linkStep === 'password';
  const isLinkCode = activeMode === 'link' && linkStep === 'code';

  return (
    <div className="auth-page signin-page">
      <div className="auth-page-accent" aria-hidden="true" />

      <div className="page-header">
        <div className="logo-bubble">
          <div className="logo">
            <img className="logo-img" src="/assets/logo2.webp" alt="Olitask" />
          </div>
        </div>
        <h1>{isSetupMode ? 'Complete Apple setup' : 'Link your Apple account'}</h1>
        <p className="subtitle">
          {isSetupMode ? 'Finish creating your Apple sign-in profile' : 'Confirm how you want to connect this Apple ID'}
        </p>
      </div>

      <div className="auth-card signin-card" style={{ maxWidth: 560 }}>
        <div className={`error-banner ${error ? 'show' : ''}`}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{error}</span>
        </div>

        {isSetupMode ? (
          <form onSubmit={handleSetupSubmit}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
              <img
                src={avatarSrc}
                alt="Apple profile"
                style={{
                  width: 58,
                  height: 58,
                  borderRadius: '999px',
                  objectFit: 'cover',
                  border: '1px solid rgba(34, 197, 94, 0.16)',
                  background: '#fff',
                }}
              />
              <div>
                <div style={{ fontWeight: 800, color: '#1a1f2e' }}>
                  {userInfo.name || initialDisplayName || 'Apple user'}
                </div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                  Enter your details to finish setup
                </div>
              </div>
            </div>

            <div className={`field ${error && !email ? 'has-error' : ''}`}>
              <label>Email</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoComplete="email"
              />
              <div className="error-msg">Please enter a valid email address</div>
            </div>

            <div className="field">
              <label>First name</label>
              <input
                type="text"
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={loading}
                autoComplete="given-name"
              />
            </div>

            <div className="field">
              <label>Last name</label>
              <input
                type="text"
                placeholder="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={loading}
                autoComplete="family-name"
              />
            </div>

            <div className="field">
              <label>Phone <span className="opt">(optional)</span></label>
              <input
                type="tel"
                placeholder="+256 700 000000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={loading}
                autoComplete="tel"
              />
            </div>

            <div className={`field ${error && !password ? 'has-error' : ''}`}>
              <label>Password</label>
              <div className="pw-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="pw-toggle"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
              <div className="error-msg">Please create a password</div>
            </div>

            <div className={`field ${error && !confirmPassword ? 'has-error' : ''}`}>
              <label>Confirm password</label>
              <div className="pw-wrap">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="pw-toggle"
                  onClick={() => setShowConfirmPassword((value) => !value)}
                  aria-label={showConfirmPassword ? 'Hide password confirmation' : 'Show password confirmation'}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
              <div className="error-msg">Please confirm your password</div>
            </div>

            <div className="btn-row">
              <Link to="/signin" className="btn btn-cancel">
                Cancel
              </Link>
              <button type="submit" className="btn btn-login" disabled={loading}>
                {loading ? 'Creating account...' : 'Continue'}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
              <img
                src={avatarSrc}
                alt="Apple profile"
                style={{
                  width: 58,
                  height: 58,
                  borderRadius: '999px',
                  objectFit: 'cover',
                  border: '1px solid rgba(34, 197, 94, 0.16)',
                  background: '#fff',
                }}
              />
              <div>
                <div style={{ fontWeight: 800, color: '#1a1f2e' }}>
                  {email || userInfo.email || 'Existing account'}
                </div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                  {state.message || 'We found an existing account that matches this Apple sign-in.'}
                </div>
              </div>
            </div>

            <div style={{ background: '#f8fafc', border: '1px solid rgba(34, 197, 94, 0.12)', borderRadius: 14, padding: 16, marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1f2e', lineHeight: 1.6 }}>
                {isLinkConfirm && 'Confirm that you want to link this Apple ID to the account above.'}
                {isLinkPassword && 'Enter your password to confirm the link.'}
                {isLinkCode && 'Enter the 6-digit verification code sent by the backend to finish linking.'}
              </div>
            </div>

            {isLinkConfirm ? (
              <>
                <div className="field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    autoComplete="email"
                    placeholder="you@example.com"
                  />
                </div>

                <div className="btn-row">
                  <button
                    type="button"
                    className="btn btn-cancel"
                    onClick={() => navigate('/signin')}
                    disabled={loading}
                  >
                    Deny
                  </button>
                  <button type="button" className="btn btn-login" onClick={handleStartLink} disabled={loading}>
                    {loading ? 'Linking...' : 'Accept & Link'}
                  </button>
                </div>
              </>
            ) : null}

            {isLinkPassword ? (
              <form onSubmit={handlePasswordSubmit}>
                <div className="field">
                  <label>Password</label>
                  <div className="pw-wrap">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="pw-toggle"
                      onClick={() => setShowPassword((value) => !value)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="btn-row">
                  <button
                    type="button"
                    className="btn btn-cancel"
                    onClick={() => setLinkStep('confirm')}
                    disabled={loading}
                  >
                    Back
                  </button>
                  <button type="submit" className="btn btn-login" disabled={loading}>
                    {loading ? 'Verifying...' : 'Continue'}
                  </button>
                </div>
              </form>
            ) : null}

            {isLinkCode ? (
              <form onSubmit={handleCodeSubmit}>
                <div className="field">
                  <label>Verification code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="123456"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/[^\d]/g, ''))}
                    disabled={loading}
                    autoComplete="one-time-code"
                  />
                </div>

                <div className="btn-row">
                  <button
                    type="button"
                    className="btn btn-cancel"
                    onClick={() => setLinkStep('password')}
                    disabled={loading}
                  >
                    Back
                  </button>
                  <button type="submit" className="btn btn-login" disabled={loading}>
                    {loading ? 'Verifying...' : 'Confirm link'}
                  </button>
                </div>
              </form>
            ) : null}

            <div style={{ marginTop: 10, textAlign: 'center', fontSize: 13, color: '#6b7280' }}>
              <Link to="/signin" style={{ color: '#1e9e45', fontWeight: 700 }}>
                Return to sign in
              </Link>
            </div>
          </>
        )}
      </div>

      <div className="wave-bg" aria-hidden="true">
        <span />
      </div>
    </div>
  );
};

export default AppleSetup;